import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Apple, Smartphone, Check, Loader2, Gift, Copy, CheckCheck, CreditCard } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function SubscribeDialog({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(null); // 'stripe-monthly' | 'stripe-annual' | 'apple' | 'google' | null
  const [referral, setReferral] = useState(null); // {code, share_url, count, credits_months}
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/referral/me");
        if (!cancelled) setReferral(data);
      } catch (_) {
        if (!cancelled) setReferral(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const copyShareLink = async () => {
    if (!referral?.share_url) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const subscribeStripe = async (plan) => {
    setLoading(`stripe-${plan}`);
    try {
      const { data } = await api.post("/subscriptions/stripe/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("No se pudo iniciar el checkout.");
        setLoading(null);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (typeof detail === "object" && detail?.message) ||
        (typeof detail === "string" && detail) ||
        "No se pudo iniciar Stripe. Inténtalo de nuevo.";
      toast.error(msg);
      setLoading(null);
    }
  };

  const subscribe = async (provider) => {
    setLoading(provider);
    try {
      const endpoint =
        provider === "apple"
          ? "/subscription/verify-apple"
          : "/subscription/verify-google";
      // STUB receipt — real Apple/Google verification will replace this later.
      await api.post(endpoint, {
        receipt: "mock_receipt",
        purchase_token: "mock_token",
        product_id: "finly_monthly",
      });
      await refresh();
      toast.success("¡Bienvenido a Premium!");
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo activar tu suscripción. Inténtalo de nuevo.");
    } finally {
      setLoading(null);
    }
  };

  const perks = [
    "Proyección de fin de mes en tiempo real",
    "Dinero disponible por día",
    "Recomendaciones con consecuencias claras",
    "Acceso completo a tu historial y gráficas",
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#131C2E] max-w-md p-0 overflow-hidden"
        data-testid="subscribe-dialog"
      >
        <div className="bg-gradient-to-br from-[#0052FF] via-[#1E40AF] to-[#0B1437] p-6 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              Finly+ Premium
            </DialogTitle>
            <DialogDescription className="text-white/80 text-sm">
              Mantén el control real de tus finanzas — todos los meses.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex items-baseline gap-3">
            <div>
              <span className="font-display text-4xl font-bold">$3.00</span>
              <span className="text-white/70 text-sm ml-1">USD / mes</span>
            </div>
            <div className="text-xs text-white/70">o $30.00 USD/año (ahorra 17%)</div>
          </div>
        </div>

        <div className="p-6 space-y-3">
          {perks.map((p, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="w-5 h-5 rounded-full bg-[#00E590]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-3 h-3 text-[#00C97D]" />
              </div>
              <span className="text-sm text-[#F1F5F9]">{p}</span>
            </div>
          ))}

          <div className="pt-4 space-y-2.5">
            <Button
              onClick={() => subscribeStripe("monthly")}
              disabled={loading !== null}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white h-11"
              data-testid="subscribe-stripe-monthly-btn"
            >
              {loading === "stripe-monthly" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Pagar con tarjeta — $3.00/mes
            </Button>
            <Button
              onClick={() => subscribeStripe("annual")}
              disabled={loading !== null}
              variant="outline"
              className="w-full border-[#3B82F6]/40 bg-transparent text-[#3B82F6] hover:bg-[#3B82F6]/10 h-11"
              data-testid="subscribe-stripe-annual-btn"
            >
              {loading === "stripe-annual" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Anual — $30.00 USD (ahorra 17%)
            </Button>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#2A3447]" /></div>
              <div className="relative flex justify-center"><span className="bg-[#131C2E] px-2 text-[10px] uppercase tracking-wider text-[#94A3B8]">o desde la app móvil</span></div>
            </div>

            <Button
              onClick={() => subscribe("apple")}
              disabled={loading !== null}
              className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white h-11"
              data-testid="subscribe-apple-btn"
            >
              {loading === "apple" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Apple className="w-4 h-4 mr-2" />
              )}
              Suscribirme con Apple
            </Button>
            <Button
              onClick={() => subscribe("google")}
              disabled={loading !== null}
              className="w-full bg-[#0052FF] hover:bg-[#0040CC] text-white h-11"
              data-testid="subscribe-google-btn"
            >
              {loading === "google" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Smartphone className="w-4 h-4 mr-2" />
              )}
              Suscribirme con Google
            </Button>
          </div>

          <p className="text-[11px] text-[#94A3B8] text-center pt-2">
            * Activación inmediata. Renovación gestionada por tu tienda de apps.
          </p>

          {/* Referral share block */}
          <div
            className="mt-2 rounded-lg border border-[#00E590]/30 bg-gradient-to-br from-[#00E590]/8 to-[#0052FF]/5 p-4"
            data-testid="referral-share-block"
          >
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-md bg-[#00E590]/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-4 h-4 text-[#00C97D]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#F1F5F9] leading-tight">
                  Comparte Finly+ y gana 1 mes gratis
                </p>
                <p className="text-[11px] text-[#64748B] mt-1 leading-relaxed">
                  Por cada amig@ que se suscriba con tu código, recibes 30 días extra de Premium.
                </p>
              </div>
            </div>

            {referral ? (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 px-3 py-2 bg-[#131C2E] border border-[#2A3447] rounded-md font-mono text-sm tracking-widest text-[#F1F5F9] truncate"
                    data-testid="referral-code-display"
                  >
                    {referral.code}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyShareLink}
                    className="h-9 border-[#0052FF]/30 text-[#0052FF] hover:bg-[#0052FF]/5"
                    data-testid="referral-copy-btn"
                  >
                    {copied ? (
                      <>
                        <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar enlace
                      </>
                    )}
                  </Button>
                </div>
                {referral.count > 0 && (
                  <p
                    className="text-[11px] text-[#0052FF] font-medium"
                    data-testid="referral-stats"
                  >
                    🎁 Has invitado a {referral.count} {referral.count === 1 ? "amig@" : "amig@s"} ·{" "}
                    {referral.credits_months} {referral.credits_months === 1 ? "mes" : "meses"} ganados
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-3 h-9 rounded-md bg-white/50 animate-pulse" />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
